<template>
  <section class="export-panel">
    <h6>Audit Export</h6>
    <p class="export-note">Download this record summary and timeline as CSV or PDF.</p>
    <div class="export-actions">
      <button class="btn-ghost" @click="exportCsv">Export CSV</button>
      <button class="btn-primary" @click="exportPdf">Export PDF</button>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FleetAuditExport',
  props: {
    filePrefix: {
      type: String,
      default: 'fleet-audit',
    },
    reportTitle: {
      type: String,
      default: 'Fleet Audit Report',
    },
    summaryRows: {
      type: Array,
      default: () => [],
    },
    timelineItems: {
      type: Array,
      default: () => [],
    },
    reportId: {
      type: String,
      default: '',
    },
    reviewedBy: {
      type: String,
      default: '',
    },
    reviewedDate: {
      type: String,
      default: '',
    },
  },
  methods: {
    sanitizeFileName(v) {
      return String(v || 'fleet-audit')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },
    csvCell(v) {
      const text = String(v ?? '');
      if (text.includes(',') || text.includes('"') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    },
    resolvedReportId() {
      if (this.reportId) return this.reportId;
      const stamp = new Date().toISOString().replace(/[-:]/g, '').slice(0, 15);
      return `${this.sanitizeFileName(this.filePrefix).toUpperCase()}-${stamp}`;
    },
    resolvedReviewedBy() {
      return this.reviewedBy || 'System Reviewer';
    },
    resolvedReviewedDate() {
      return this.reviewedDate || new Date().toLocaleDateString();
    },
    async buildVerificationQr(payload) {
      const qrModule = await import('qrcode-generator');
      const qrFactory = qrModule?.default || qrModule;
      const qr = qrFactory(0, 'M');
      qr.addData(payload);
      qr.make();
      return qr.createDataURL(5, 8);
    },
    exportCsv() {
      const lines = [];
      lines.push(this.reportTitle);
      lines.push(`Report ID,${this.csvCell(this.resolvedReportId())}`);
      lines.push(`Generated At,${new Date().toISOString()}`);
      lines.push(`Reviewed By,${this.csvCell(this.resolvedReviewedBy())}`);
      lines.push(`Reviewed Date,${this.csvCell(this.resolvedReviewedDate())}`);
      lines.push('');
      lines.push('Summary');
      lines.push('Field,Value');

      this.summaryRows.forEach((row) => {
        lines.push(`${this.csvCell(row.label)},${this.csvCell(row.value)}`);
      });

      lines.push('');
      lines.push('Timeline');
      lines.push('Event,Time,Note');

      this.timelineItems.forEach((item) => {
        lines.push(
          `${this.csvCell(item.label)},${this.csvCell(item.time || '')},${this.csvCell(item.note || '')}`,
        );
      });

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.sanitizeFileName(this.filePrefix)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      this.$toast?.success?.('CSV exported');
    },
    async exportPdf() {
      try {
        const { default: html2pdf } = await import('html2pdf.js');
        const generatedAtIso = new Date().toISOString();
        const resolvedReportId = this.resolvedReportId();

        const wrapper = document.createElement('div');
        wrapper.style.padding = '16px';
        wrapper.style.fontFamily = 'Arial, sans-serif';
        wrapper.style.fontSize = '12px';
        wrapper.style.color = '#111827';

        const title = document.createElement('h2');
        title.textContent = this.reportTitle;
        wrapper.appendChild(title);

        const reportId = document.createElement('p');
        reportId.style.margin = '4px 0';
        reportId.textContent = `Report ID: ${resolvedReportId}`;
        wrapper.appendChild(reportId);

        const generated = document.createElement('p');
        generated.textContent = `Generated: ${new Date(generatedAtIso).toLocaleString()}`;
        wrapper.appendChild(generated);

        const summaryTitle = document.createElement('h3');
        summaryTitle.textContent = 'Summary';
        wrapper.appendChild(summaryTitle);

        const summaryList = document.createElement('ul');
        this.summaryRows.forEach((row) => {
          const li = document.createElement('li');
          li.textContent = `${row.label}: ${row.value}`;
          summaryList.appendChild(li);
        });
        wrapper.appendChild(summaryList);

        const timelineTitle = document.createElement('h3');
        timelineTitle.textContent = 'Timeline';
        wrapper.appendChild(timelineTitle);

        const timelineList = document.createElement('ol');
        this.timelineItems.forEach((item) => {
          const li = document.createElement('li');
          li.style.marginBottom = '8px';
          li.textContent = `${item.label} | ${item.time || 'N/A'} | ${item.note || ''}`;
          timelineList.appendChild(li);
        });
        wrapper.appendChild(timelineList);

        const footer = document.createElement('div');
        footer.style.marginTop = '20px';
        footer.style.paddingTop = '10px';
        footer.style.borderTop = '1px solid #d1d5db';

        const signoffTitle = document.createElement('h3');
        signoffTitle.textContent = 'Digital Sign-Off';
        footer.appendChild(signoffTitle);

        const reviewedBy = document.createElement('p');
        reviewedBy.textContent = `Reviewed By: ${this.resolvedReviewedBy()}`;
        footer.appendChild(reviewedBy);

        const reviewedDate = document.createElement('p');
        reviewedDate.textContent = `Reviewed Date: ${this.resolvedReviewedDate()}`;
        footer.appendChild(reviewedDate);

        const complianceNote = document.createElement('p');
        complianceNote.style.fontSize = '11px';
        complianceNote.style.color = '#4b5563';
        complianceNote.textContent = 'This report is system-generated for formal compliance filing and audit traceability.';
        footer.appendChild(complianceNote);

        const verificationRow = document.createElement('div');
        verificationRow.style.marginTop = '12px';
        verificationRow.style.display = 'flex';
        verificationRow.style.alignItems = 'center';
        verificationRow.style.gap = '10px';

        const qrLabel = document.createElement('div');
        qrLabel.style.fontSize = '11px';
        qrLabel.style.color = '#374151';
        qrLabel.textContent = 'Verification QR (encodes report ID + timestamp)';

        try {
          const qrPayload = `${resolvedReportId}|${generatedAtIso}`;
          const qrDataUrl = await this.buildVerificationQr(qrPayload);
          const qrImage = document.createElement('img');
          qrImage.src = qrDataUrl;
          qrImage.alt = 'Report verification QR';
          qrImage.width = 72;
          qrImage.height = 72;
          qrImage.style.border = '1px solid #d1d5db';
          qrImage.style.borderRadius = '4px';
          verificationRow.appendChild(qrImage);
        } catch (_) {
          qrLabel.textContent = 'Verification QR unavailable for this export run.';
        }

        verificationRow.appendChild(qrLabel);
        footer.appendChild(verificationRow);

        wrapper.appendChild(footer);

        await html2pdf()
          .set({
            margin: 10,
            filename: `${this.sanitizeFileName(this.filePrefix)}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          })
          .from(wrapper)
          .save();

        this.$toast?.success?.('PDF exported');
      } catch (err) {
        this.$toast?.error?.(err?.message || 'PDF export failed');
      }
    },
  },
};
</script>

<style scoped>
.export-panel {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(11, 110, 243, 0.16);
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 10px 22px rgba(17, 35, 64, 0.06);
}

.export-panel h3 {
  margin: 0 0 8px;
  font-size: 1rem;
  color: #16385e;
  font-weight: 800;
}

.export-note {
  margin: 0;
  color: #5f7897;
  font-size: 12px;
}

.export-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
</style>
